/**
 * F17 T17.1.5 — wrapper API calls dla buildera procesu.
 *
 * Pozycje X/Y w DB są DECIMAL — backend zwraca je jako string. Wrapper
 * `getProcess` konwertuje na number przy odbiorze i odsyła jako number
 * w `patchGraph` (Zod schema przyjmuje `z.number().finite()`).
 *
 * Lokalizacja: `lib/processes/` (osobny folder per moduł).
 */

import { PROCESS_GRAPH_SCHEMA_VERSION, type PublishGraphIssue } from '@saas/shared';

import type { ApproverConfigValue } from '@/components/process-builder/ApproverPicker';
import type {
  ConditionConfig,
  ConditionConfigByClass,
} from '@/components/process-builder/ConditionNode';
import type { ProcessEdgeType } from '@/components/process-builder/graphTypes';
import { api } from '@/lib/api';

// ============================================================================
// Domain types — odpowiedź GET /api/processes/:id
// ============================================================================

export interface ProcessRecord {
  id: string;
  number: number;
  name: string;
  description: string | null;
  targetModuleCode: string;
  triggerType: 'manual';
  isActive: boolean;
  isPublished: boolean;
  isLinked: boolean;
  version: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GraphNodeRecord {
  id: string;
  processId: string;
  nodeType: 'start' | 'decision' | 'parallel_split' | 'parallel_join' | 'end';
  label: string | null;
  approverConfig: ApproverConfigValue | Record<string, never>;
  /** „Pozwól cofnąć do poprzedniego etapu" — tylko etapy decyzji; reszta zawsze `false`. */
  allowStepBack: boolean;
  // Pola wymagane żyją na drodze (`GraphEdgeRecord.requiredFields`); kolumna węzła została
  // usunięta migracją PUB 0180, więc węzeł jej nie niesie.
  positionX: string;
  positionY: string;
  createdAt: string;
  updatedAt: string;
}

export interface GraphEdgeRecord {
  id: string;
  processId: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType: ProcessEdgeType;
  /** Płaskie drzewo dla większości modułów; mapa per klasa dokumentu (F.3 follow-up)
   * TYLKO gdy proces celuje w `crm_core.documents` — patrz ConditionEditor. */
  conditionConfig: ConditionConfig | ConditionConfigByClass | Record<string, never> | null;
  label: string | null;
  /** Kolejność dróg w obrębie etapu źródłowego. */
  sortOrder: number;
  /** Droga domyślna etapu. */
  isDefault: boolean;
  /** Wybór drogi wymaga komentarza. */
  requireComment: boolean;
  /** Pola wymagane do wybrania drogi (klucz = klasa dokumentu albo `"default"`); `null` = brak. */
  requiredFields: Record<string, string[]> | null;
  /** ADR-0074 — procedura walidacji przypięta do krawędzi (`null` = brak wiązania). */
  validationProcedureId: string | null;
  /** Snapshot wartości parametrów procedury walidacji dla tej krawędzi. */
  validationParams: Record<string, unknown>;
  /** ADR-0074 — procedura akcji przypięta do krawędzi (`null` = brak wiązania). */
  actionProcedureId: string | null;
  /** Snapshot wartości parametrów procedury akcji dla tej krawędzi. */
  actionParams: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessWithGraph {
  process: ProcessRecord;
  nodes: GraphNodeRecord[];
  edges: GraphEdgeRecord[];
  /**
   * Klasyfikatory (F17 — allow-lista widoczności) — gołe UUID typów dokumentów
   * (`crm_core.documents`) albo kategorii (pozostałe osie). Backend dopasowuje
   * PO SAMYM id — nigdy złożonego `id:companyId` (patrz `ProcessVisibilityView`).
   *
   * Stoją OBOK `process`, nie w środku: `process` to surowy wiersz tabeli `processes`, a
   * klasyfikatory żyją w osobnej tabeli i trasa dokłada je do odpowiedzi jako własne pola
   * (`apps/api/app/api/processes/[id]/route.ts`). Wcześniej ten typ deklarował je wewnątrz
   * `process` — kompilator milczał, a panel odczytywał `undefined` i rysował pustą listę
   * mimo poprawnie zapisanych danych.
   */
  classifierIds: string[];
  /** „Bez kategorii" — dozwolone TYLKO dla osi niebędącej dokumentami. */
  includeUncategorized: boolean;
}

// ============================================================================
// Domain types — body PATCH /api/processes/:id/graph
// ============================================================================

export interface GraphNodeInput {
  clientId: string;
  nodeType: GraphNodeRecord['nodeType'];
  label?: string;
  approverConfig?: ApproverConfigValue;
  /** Tylko etap decyzji; brak = `false`. */
  allowStepBack?: boolean;
  positionX: number;
  positionY: number;
}

export interface GraphEdgeInput {
  sourceClientId: string;
  targetClientId: string;
  edgeType: ProcessEdgeType;
  conditionConfig?: ConditionConfig | ConditionConfigByClass;
  label?: string;
  /** Kolejność dróg w obrębie etapu źródłowego (od 0 per etap). */
  sortOrder?: number;
  /**
   * Droga domyślna etapu: jedna zwykła albo kilka warunkowych, bez mieszania (pilnuje publikacja);
   * przy kilku widocznych obowiązuje pierwsza wg kolejności.
   */
  isDefault?: boolean;
  /** Wybór drogi wymaga komentarza. */
  requireComment?: boolean;
  /** Pola wymagane do wybrania drogi (klucz = klasa dokumentu albo `"default"`). */
  requiredFields?: Record<string, string[]> | null;
  /** ADR-0074 — procedura walidacji przypięta do krawędzi (`null` = odczep wiązania). */
  validationProcedureId?: string | null;
  validationParams?: Record<string, unknown>;
  /** ADR-0074 — procedura akcji przypięta do krawędzi (`null` = odczep wiązania). */
  actionProcedureId?: string | null;
  actionParams?: Record<string, unknown>;
}

export interface PatchGraphResponse {
  nodeIdMap: Record<string, string>;
  nodeCount: number;
  edgeCount: number;
}

// ============================================================================
// Listing (GET /api/processes) — paginacja + filtry
// ============================================================================

export interface ListProcessesQuery {
  q?: string;
  isPublished?: boolean;
  isActive?: boolean;
  targetModuleCode?: string;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
  /**
   * Filtry per kolumna z popupu w nagłówku (Kreator Listy) — JSON.stringify
   * `HeaderFilter[]`, patrz `serializeHeaderFiltersForRequest` w
   * `ProcessesListPage.tsx`. Kontrakt slug↔kolumna z whitelistą
   * `HEADER_FILTER_FIELDS` w `apps/api/app/api/processes/route.ts`.
   */
  headerFilters?: string;
}

/** Wiersz listingu — `processes` row z dodatkową kolumną `gcrecord`.
 *  `sourceTemplateId` jest opcjonalne — backend zwraca pole dopiero po
 *  rozszerzeniu GET /api/processes (T17.11.7 follow-up). */
export interface ProcessListRow extends ProcessRecord {
  gcrecord: string | null;
  /** UUID szablonu z ADM, jeśli proces pochodzi z biblioteki wzorów. */
  sourceTemplateId?: string | null;
}

export interface ListProcessesResponse {
  data: ProcessListRow[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /api/processes — lista definicji procesów (default sort: number ASC). */
export async function listProcesses(
  query: ListProcessesQuery = {},
): Promise<ListProcessesResponse> {
  const params: Record<string, string> = {};
  if (query.q) params.q = query.q;
  if (query.isPublished !== undefined) params.isPublished = String(query.isPublished);
  if (query.isActive !== undefined) params.isActive = String(query.isActive);
  if (query.targetModuleCode) params.targetModuleCode = query.targetModuleCode;
  if (query.includeDeleted) params.includeDeleted = 'true';
  if (query.limit !== undefined) params.limit = String(query.limit);
  if (query.offset !== undefined) params.offset = String(query.offset);
  if (query.headerFilters) params.headerFilters = query.headerFilters;
  const res = await api.get<ListProcessesResponse>('/processes', { params });
  return res.data;
}

// ============================================================================
// POST /api/processes — tworzenie definicji (draft, isPublished=false)
// ============================================================================

export interface CreateProcessInput {
  name: string;
  description?: string | null;
  targetModuleCode: string;
}

/** POST /api/processes — tworzy nowy draft procesu. Zwraca metadane bez grafu. */
export async function createProcess(input: CreateProcessInput): Promise<ProcessRecord> {
  const res = await api.post<ProcessRecord>('/processes', input);
  return res.data;
}

// ============================================================================
// DELETE /api/processes/:id — soft-delete (guard: brak aktywnych instancji)
// ============================================================================

export interface DeleteProcessSuccess {
  ok: true;
}

export interface DeleteProcessFailure {
  ok: false;
  error: 'PROCESS_HAS_ACTIVE_INSTANCES' | 'ALREADY_DELETED' | 'PROCESS_NOT_FOUND' | 'UNKNOWN';
  /** Liczba aktywnych instancji (gdy `error='PROCESS_HAS_ACTIVE_INSTANCES'`). */
  count?: number;
}

/**
 * DELETE /api/processes/:id — soft-delete (gcrecord=now()). Backend rzuca 409
 * `PROCESS_HAS_ACTIVE_INSTANCES` jeśli istnieją aktywne instancje (`status='active'`,
 * `gcrecord IS NULL`). Wrapper mapuje na discriminated union.
 */
export async function deleteProcess(
  id: string,
  reason?: string,
): Promise<DeleteProcessSuccess | DeleteProcessFailure> {
  try {
    const url = reason
      ? `/processes/${id}?reason=${encodeURIComponent(reason)}`
      : `/processes/${id}`;
    await api.delete(url);
    return { ok: true };
  } catch (err: unknown) {
    const ax = err as {
      response?: {
        status?: number;
        data?: { error?: string; count?: number };
      };
    };
    const status = ax.response?.status;
    const data = ax.response?.data;
    if (status === 409 && data?.error === 'PROCESS_HAS_ACTIVE_INSTANCES') {
      return {
        ok: false,
        error: 'PROCESS_HAS_ACTIVE_INSTANCES',
        count: data.count,
      };
    }
    if (status === 409 && data?.error === 'ALREADY_DELETED') {
      return { ok: false, error: 'ALREADY_DELETED' };
    }
    if (status === 404 && data?.error === 'PROCESS_NOT_FOUND') {
      return { ok: false, error: 'PROCESS_NOT_FOUND' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}

// ============================================================================
// API calls — single process operations
// ============================================================================

/** GET /api/processes/:id — pobiera definicję + graf w jednym requestie. */
export async function getProcess(id: string): Promise<ProcessWithGraph> {
  const res = await api.get<ProcessWithGraph>(`/processes/${id}`);
  return res.data;
}

// ============================================================================
// PUT /api/processes/:id/classifiers — replace allow-listy klasyfikatorów
// ============================================================================

export interface ProcessClassifiersInput {
  /** Gołe UUID typów dokumentów albo kategorii — nigdy `id:companyId`. */
  classifierIds: string[];
  /** Dozwolone tylko dla osi niebędącej dokumentami (`crm_core.documents` → zawsze false). */
  includeUncategorized: boolean;
}

/**
 * PUT /api/processes/:id/classifiers — pełny replace listy dozwolonych
 * klasyfikatorów procesu (F17 — filtrowanie pickera „Skieruj do akceptacji"
 * po typie dokumentu / kategorii). Zwraca zapisany stan 1:1 z body
 * (`{ classifierIds, includeUncategorized }`) — patrz PUT w
 * `apps/api/app/api/processes/[id]/classifiers/route.ts`.
 */
export async function putProcessClassifiers(
  id: string,
  body: ProcessClassifiersInput,
): Promise<ProcessClassifiersInput> {
  const res = await api.put<ProcessClassifiersInput>(`/processes/${id}/classifiers`, body);
  return res.data;
}

/**
 * PATCH /api/processes/:id/graph — full-replace grafu.
 *
 * Backend kasuje stare węzły + krawędzie i wstawia nowe atomicznie. Zwraca
 * mapowanie `clientId → dbId` (UUID) — komponent wywołujący powinien
 * dispatch'ować `MARK_SAVED` z tym mapowaniem żeby zsynchronizować lokalne IDs.
 *
 * Zapis OPUBLIKOWANEGO procesu łamiący reguły publikacji serwer odrzuca z
 * `422 GRAPH_VALIDATION_FAILED` (graf bez zmian) — powody czyta `getGraphValidationFailure`.
 * Szkic zapisuje się bez tych reguł.
 */
export async function patchGraph(
  id: string,
  body: { nodes: GraphNodeInput[]; edges: GraphEdgeInput[] },
): Promise<PatchGraphResponse> {
  // Wersja kształtu zapisu — serwer odrzuca zapis z nieodświeżonej karty (409 GRAPH_SCHEMA_OUTDATED).
  const res = await api.patch<PatchGraphResponse>(`/processes/${id}/graph`, {
    graphSchemaVersion: PROCESS_GRAPH_SCHEMA_VERSION,
    ...body,
  });
  return res.data;
}

/**
 * Czy odrzucony zapis grafu (procesu albo szablonu) to `409 GRAPH_SCHEMA_OUTDATED` — kreator
 * otwarty przed wdrożeniem nowego kształtu zapisu. Zapis grafu to pełna podmiana, więc zamiast
 * po cichu skasować ustawienia dróg serwer prosi o odświeżenie strony; strony kreatorów pokazują
 * wtedy czytelny komunikat (`tenant.processes.builder.toast.graphSchemaOutdated`).
 */
export function isGraphSchemaOutdatedError(err: unknown): boolean {
  const ax = err as { response?: { status?: number; data?: { error?: string } } } | null;
  return ax?.response?.status === 409 && ax.response.data?.error === 'GRAPH_SCHEMA_OUTDATED';
}

// ============================================================================
// POST /api/processes/:id/publish
// ============================================================================

export interface PublishSuccess {
  ok: true;
  process: ProcessRecord;
}

/**
 * Możliwe powody odrzucenia publikacji albo zapisu opublikowanego procesu (422
 * GRAPH_VALIDATION_FAILED) — ta sama unia co po stronie serwera (`PublishGraphIssue` ze
 * `@saas/shared`): start / koniec / akceptanci + reguły etapów i dróg, które sprawdza też kreator
 * przed publikacją (`validateStageRoutes`).
 */
export type PublishIssue = PublishGraphIssue;

export interface PublishFailure {
  ok: false;
  error: 'GRAPH_VALIDATION_FAILED' | 'ALREADY_PUBLISHED' | 'UNKNOWN';
  issues?: PublishIssue[];
  decisionNodesWithoutApprover?: string[];
}

/** Treść odpowiedzi `422 GRAPH_VALIDATION_FAILED` (publikacja i zapis opublikowanego procesu). */
export interface GraphValidationFailure {
  issues: PublishIssue[];
  decisionNodesWithoutApprover?: string[];
}

/**
 * Czy odrzucone żądanie (publikacja albo zapis grafu OPUBLIKOWANEGO procesu) to
 * `422 GRAPH_VALIDATION_FAILED` — serwer stosuje do obu tych operacji te same reguły i oddaje
 * ten sam kształt (`issues` + listy węzłów). Zwraca powody albo `null` dla każdego innego błędu.
 * Jeden parser dla `publishProcess` i dla kreatora (komunikat po odrzuconym zapisie).
 */
export function getGraphValidationFailure(err: unknown): GraphValidationFailure | null {
  const ax = err as {
    response?: {
      status?: number;
      data?: {
        error?: string;
        issues?: PublishIssue[];
        decisionNodesWithoutApprover?: string[];
      };
    };
  } | null;
  const status = ax?.response?.status;
  const data = ax?.response?.data;
  if (status !== 422 || data?.error !== 'GRAPH_VALIDATION_FAILED') return null;
  return {
    issues: data.issues ?? [],
    decisionNodesWithoutApprover: data.decisionNodesWithoutApprover,
  };
}

/**
 * POST /api/processes/:id/publish — opublikuj proces.
 *
 * Zwraca jednolity discriminated union `{ok: true|false}` — komponenty
 * wywołujące nie muszą rozpakowywać AxiosError (interceptor już ogarnia
 * 401/refresh). 4xx odpowiedzi serwera są mapowane na `PublishFailure`.
 */
export async function publishProcess(id: string): Promise<PublishSuccess | PublishFailure> {
  try {
    const res = await api.post<ProcessRecord>(`/processes/${id}/publish`);
    return { ok: true, process: res.data };
  } catch (err: unknown) {
    // Axios error → mapuj 422 / 409 → PublishFailure.
    const validationFailure = getGraphValidationFailure(err);
    if (validationFailure) {
      return {
        ok: false,
        error: 'GRAPH_VALIDATION_FAILED',
        issues: validationFailure.issues,
        decisionNodesWithoutApprover: validationFailure.decisionNodesWithoutApprover,
      };
    }
    const ax = err as { response?: { status?: number; data?: { error?: string } } };
    const status = ax.response?.status;
    const data = ax.response?.data;
    if (status === 409 && data?.error === 'ALREADY_PUBLISHED') {
      return { ok: false, error: 'ALREADY_PUBLISHED' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}

// ============================================================================
// POST /api/processes/:id/detach
// ============================================================================

export interface DetachSuccess {
  ok: true;
  processId: string;
}

export interface DetachFailure {
  ok: false;
  error: 'NOT_LINKED' | 'TEMPLATE_UNAVAILABLE' | 'PERMISSION_DENIED' | 'UNKNOWN';
}

/**
 * POST /api/processes/:id/detach — odepnij powiazany proces od szablonu ADM
 * i zmaterializuj graf lokalnie jako edytowalny szkic.
 *
 * Zwraca discriminated union {ok: true|false} — analogicznie do publishProcess.
 */
export async function detachProcess(id: string): Promise<DetachSuccess | DetachFailure> {
  try {
    const res = await api.post<{ processId: string }>(`/processes/${id}/detach`);
    return { ok: true, processId: res.data.processId };
  } catch (err: unknown) {
    const ax = err as {
      response?: { status?: number; data?: { error?: string } };
    };
    const status = ax.response?.status;
    const data = ax.response?.data;

    if (status === 409 && data?.error === 'NOT_LINKED') {
      return { ok: false, error: 'NOT_LINKED' };
    }
    if (status === 409 && data?.error === 'TEMPLATE_UNAVAILABLE') {
      return { ok: false, error: 'TEMPLATE_UNAVAILABLE' };
    }
    if (status === 403) {
      return { ok: false, error: 'PERMISSION_DENIED' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}
